// Serviço para gerenciamento centralizado da logo

export interface LogoConfig {
  current: string;
  backup: string;
  lastUpdated: string;
  version: number;
}

class LogoService {
  private static instance: LogoService;
  private storageKey = 'arcgel_logo_config';
  private defaultConfig: LogoConfig = {
    current: '/logo.png',
    backup: '/logo.png',
    lastUpdated: new Date().toISOString(),
    version: 1
  };

  private constructor() {}

  static getInstance(): LogoService {
    if (!LogoService.instance) {
      LogoService.instance = new LogoService();
    }
    return LogoService.instance;
  }

  // Obter configuração atual da logo
  getLogoConfig(): LogoConfig {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Erro ao carregar configuração da logo:', error);
    }
    return this.defaultConfig;
  }

  // Salvar nova configuração da logo
  saveLogoConfig(config: LogoConfig): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(config));
      this.notifyLogoChange(config);
    } catch (error) {
      console.error('Erro ao salvar configuração da logo:', error);
      throw new Error('Falha ao salvar configuração da logo');
    }
  }

  // Atualizar logo atual
  async updateLogo(newLogoData: string): Promise<void> {
    const currentConfig = this.getLogoConfig();
    
    const newConfig: LogoConfig = {
      current: newLogoData,
      backup: currentConfig.current,
      lastUpdated: new Date().toISOString(),
      version: currentConfig.version + 1
    };

    this.saveLogoConfig(newConfig);
    
    // Atualizar logo na documentação
    await this.updateDocumentationLogo(newLogoData);
    
    // Atualizar favicon
    this.updateFavicon(newLogoData);
  }

  // Restaurar logo anterior
  restoreLogo(): void {
    const currentConfig = this.getLogoConfig();
    
    const restoredConfig: LogoConfig = {
      current: currentConfig.backup,
      backup: currentConfig.current,
      lastUpdated: new Date().toISOString(),
      version: currentConfig.version + 1
    };

    this.saveLogoConfig(restoredConfig);
  }

  // Obter URL da logo atual
  getCurrentLogoUrl(): string {
    return this.getLogoConfig().current;
  }

  // Notificar mudança da logo para componentes
  private notifyLogoChange(config: LogoConfig): void {
    // Disparar evento customizado para notificar componentes
    const event = new CustomEvent('logoChanged', { 
      detail: config 
    });
    window.dispatchEvent(event);
  }

  // Atualizar logo na documentação
  private async updateDocumentationLogo(logoData: string): Promise<void> {
    try {
      // Converter base64 para blob se necessário
      if (logoData.startsWith('data:')) {
        const response = await fetch(logoData);
        const blob = await response.blob();
        
        // Em uma implementação real, aqui você faria upload para o servidor
        // e atualizaria o arquivo docs/logo.png
        console.log('Atualizando logo na documentação...', blob.size, 'bytes');
        
        // Simular atualização do CSS da documentação com nova logo base64
        this.updateDocumentationCSS(logoData);
      }
    } catch (error) {
      console.error('Erro ao atualizar logo na documentação:', error);
    }
  }

  // Atualizar CSS da documentação com nova logo
  private updateDocumentationCSS(logoData: string): void {
    // Em uma implementação real, isso atualizaria o arquivo docs/styles.css
    // substituindo a logo base64 no CSS
    console.log('Atualizando CSS da documentação com nova logo...');
    
    // Simular atualização
    const newCSSRule = `
      .logo-fallback {
        background-image: url('${logoData}');
      }
    `;
    
    // Aplicar temporariamente no documento atual se estivermos na documentação
    if (window.location.pathname.includes('/docs/')) {
      const style = document.createElement('style');
      style.textContent = newCSSRule;
      document.head.appendChild(style);
    }
  }

  // Atualizar favicon
  private updateFavicon(logoData: string): void {
    try {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = logoData;
      } else {
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.href = logoData;
        document.head.appendChild(newFavicon);
      }
    } catch (error) {
      console.error('Erro ao atualizar favicon:', error);
    }
  }

  // Validar arquivo de imagem
  validateImageFile(file: File): { valid: boolean; error?: string } {
    // Verificar tipo
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'Arquivo deve ser uma imagem' };
    }

    // Verificar tamanho (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      return { valid: false, error: 'Imagem deve ter no máximo 2MB' };
    }

    // Verificar extensão
    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.svg', '.webp'];
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(extension)) {
      return { valid: false, error: 'Formato não suportado. Use PNG, JPG, SVG ou WebP' };
    }

    return { valid: true };
  }

  // Converter arquivo para base64
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Obter histórico de alterações
  getLogoHistory(): Array<{ version: number; date: string; current: boolean }> {
    const config = this.getLogoConfig();
    return [
      {
        version: config.version,
        date: config.lastUpdated,
        current: true
      }
    ];
  }
}

export const logoService = LogoService.getInstance();